import React, { useEffect, useRef, useState } from "react";

interface HeadingItem {
    id: string;
    text: string;
    level: number;
}

interface Props {
    contentRef: React.RefObject<HTMLElement>;
    html: string | null;
    onItemCount?: (count: number) => void;
}

function slugify(text: string): string {
    return text.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 64) || "section";
}

export default function PageToc({ contentRef, html, onItemCount }: Props) {
    const [items, setItems] = useState<HeadingItem[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        if (!contentRef.current) { setItems([]); return; }
        const root = contentRef.current;
        const nodes = Array.from(root.querySelectorAll("h1, h2, h3")) as HTMLElement[];
        const seen = new Map<string, number>();
        const list: HeadingItem[] = nodes.map(node => {
            const text = (node.textContent || "").trim();
            let id = node.id;
            if (!id) {
                const base = slugify(text);
                const n = (seen.get(base) || 0) + 1;
                seen.set(base, n);
                id = n === 1 ? base : `${base}-${n}`;
                node.id = id;
            }
            return { id, text, level: Number(node.tagName.substring(1)) };
        });
        setItems(list);
        onItemCount?.(list.length);

        observerRef.current?.disconnect();
        if (list.length === 0) return;
        const observer = new IntersectionObserver(entries => {
            const visible = entries
                .filter(e => e.isIntersecting)
                .sort((a, b) => (a.target as HTMLElement).offsetTop - (b.target as HTMLElement).offsetTop);
            if (visible.length > 0) setActiveId(visible[0].target.id);
        }, { rootMargin: "-80px 0px -60% 0px", threshold: 0 });
        nodes.forEach(n => observer.observe(n));
        observerRef.current = observer;
        if (!activeId && list.length > 0) setActiveId(list[0].id);

        return () => { observer.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [html, contentRef]);

    if (items.length < 2) return null;

    const handleClick = (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            window.history.replaceState(null, "", `#${id}`);
            setActiveId(id);
        }
    };

    return (
        <nav className="m-toc" aria-label="Table of contents">
            <div className="m-toc-heading">On this page</div>
            <ul className="m-toc-list">
                {items.map(item => (
                    <li key={item.id} className={`m-toc-item m-toc-l${item.level}${activeId === item.id ? " active" : ""}`}>
                        <a href={`#${item.id}`} onClick={e => handleClick(e, item.id)}>
                            {item.text}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
